import { useEffect, useState } from 'react';

import CreditCardIcon from '@mui/icons-material/CreditCard';
import FeedbackIcon from '@mui/icons-material/Feedback';
import {
	Box,
	Typography,
	List,
	ListItem,
	Radio,
	Avatar,
	ListItemAvatar,
	ListItemText,
	ListItemButton,
} from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import useSWR from 'swr';

import { useAuthorize } from '~features/authentication';
import { PaymentMethodsService } from '~features/finances';
import { useDebounce } from '~hooks';
import { getInitials } from '~utils/stringUtils';

import { Notification, LoadingBar } from '../../../../components';
import { areDatesEqual } from '../../../../shared/datetime';
import { usePaymentDetails } from '../../../../shared/hooks';
import { isObject, isEmptyArray, isUndefined, isFullString } from '../../../../shared/utility';
import * as actions from '../../../../store/actions';
import { useStyles } from '../style';

interface UsageCardProps {
	selectedStartDate?: object;
	selectedEndDate?: object;
	selectedUser?: object;
	bookingData?: object;
	open?: boolean;
	fetchInstanceData?: object;
	presetData?: object;
	onFetchBookingPrice?(...args: unknown[]): unknown;
	onFetchCalculatedPrice?(...args: unknown[]): unknown;
	applyingDiscount?: boolean;
	hasChangedItem?: boolean;
	bookingLoading?: boolean;
	selectedPaymentMethod?: string;
	setSelectedPaymentMethod?(...args: unknown[]): unknown;
	setDiscount?(...args: unknown[]): unknown;
	setHasChangedItem?(...args: unknown[]): unknown;
	isProvider?: boolean;
	setApplyingDiscount?(...args: unknown[]): unknown;
	fetchBookingPrice?: boolean;
	setFetchBookingPrice?(...args: unknown[]): unknown;
	selectedItem?: object;
	setPrice?(...args: unknown[]): unknown;
	discount?: number;
	nowSwitch?: boolean;
	calculatedPrice?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	bookingPrice?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	fetchItemPolicies?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const paymentMethodsService = new PaymentMethodsService();

const UsageCard = (props: UsageCardProps) => {
	const {
		selectedUser,
		selectedItem,
		calculatedPrice,
		bookingData,
		open,
		onFetchBookingPrice,
		onFetchCalculatedPrice,
		setPrice,
		hasChangedItem,
		bookingPrice,
		selectedStartDate,
		selectedEndDate,
		fetchInstanceData,
		discount,
		applyingDiscount,
		bookingLoading,
		presetData,
		setSelectedPaymentMethod,
		selectedPaymentMethod,
		setDiscount,
		setHasChangedItem,
		isProvider,
		setApplyingDiscount,
		fetchItemPolicies,
		nowSwitch,
		fetchBookingPrice,
		setFetchBookingPrice,
	} = props;
	const { t } = useTranslation();
	const { isSuperAdmin } = useAuthorize();

	const {
		data: pmData,
		loading: pmLoading,
		error: pmError,
	} = useSWR([paymentMethodsService.basePath, { userId: selectedUser?.id }], ([_, args]) =>
		paymentMethodsService.getPaymentMethodsUnmapped(args)
	);
	const pmReady = isObject(pmData) && !pmLoading && !pmError;

	const {
		data: calculatedPriceData,
		loading: calculatedPriceLoading,
		error: calculatedPriceError,
	} = calculatedPrice;
	const priceReady =
		isObject(calculatedPriceData) && !calculatedPriceLoading && !calculatedPriceError;

	const { data: fetchItemPoliciesData } = fetchItemPolicies;

	const {
		data: bookingPriceData,
		loading: bookingPriceLoading,
		error: bookingPriceError,
	} = bookingPrice;

	const [paymentMethods, setPaymentMethods] = useState([]);

	const classes = useStyles();

	const bankData = usePaymentDetails(pmReady && isObject(pmData.personal) && pmData.personal);

	const debouncedDiscount = useDebounce(discount, 300);

	const [paymentMethodAllowed, setPaymentMethodAllowed] = useState(true);

	const isOneOffPayment =
		fetchItemPoliciesData?.allowPersonalBilling &&
		fetchItemPoliciesData?.supportedPaymentType === 'oneOffPayment';

	useEffect(() => {
		if (!isObject(bookingData)) {
			setPaymentMethods([]);
			setSelectedPaymentMethod('');
			setPaymentMethodAllowed(true);
		}
	}, [selectedUser, selectedItem, isProvider]);

	useEffect(() => {
		if (pmReady) {
			setPaymentMethods([
				...((isProvider || isSuperAdmin()) && pmData.personal.status === 'authorized' ?
					[
						{
							value: 'personal',
							logo: pmData.personal.logo,
							type: t('ui.label.privateUse'),
							...(isSuperAdmin() &&
								priceReady &&
								!calculatedPriceData.isFree && { details: bankData.bank }),
						},
					]
				:	[]),
				...(pmData.business.status === 'authorized' ?
					[
						{
							value: 'business',
							logo: pmData.business.logo,
							type: t('ui.label.businessUse'),
							details: pmData.business.name,
						},
					]
				:	[]),
			]);
		}
	}, [pmReady, priceReady]);

	const newBooking = !isObject(bookingData) && isObject(fetchItemPoliciesData);

	useEffect(() => {
		if (
			(isObject(fetchInstanceData) || newBooking) &&
			isObject(selectedUser) &&
			!calculatedPriceLoading &&
			isObject(selectedItem) &&
			selectedStartDate &&
			selectedEndDate &&
			open
		) {
			const isStartBigger = selectedStartDate.getTime() > selectedEndDate.getTime();
			const endDate =
				selectedStartDate.toISOString() === selectedEndDate.toISOString() || isStartBigger ?
					new Date(selectedStartDate.getTime() + 900000)
				:	selectedEndDate;
			const params = {
				userId: selectedUser.id,
				...(moment.isDate(selectedStartDate) && { periodStart: selectedStartDate.toISOString() }),
				periodEnd: endDate.toISOString(),
				itemInstanceId: selectedItem.instanceId,
				...(applyingDiscount && { discount: Number.isNaN(discount) ? 0 : discount }),
				...(isFullString(selectedPaymentMethod) && { billingType: selectedPaymentMethod }),
			};
			onFetchCalculatedPrice(selectedItem.itemId, params);
		}
	}, [
		selectedUser,
		selectedPaymentMethod,
		fetchInstanceData,
		selectedItem,
		selectedStartDate,
		selectedEndDate,
		open,
		debouncedDiscount,
		applyingDiscount,
		fetchItemPoliciesData,
	]);

	const timeNotChanged =
		(bookingData?.periodEnd && areDatesEqual(new Date(bookingData?.periodEnd), selectedEndDate)) ||
		(bookingData?.usage?.stop &&
			areDatesEqual(new Date(bookingData?.usage?.stop), selectedEndDate));

	useEffect(() => {
		if (isObject(bookingData) && fetchBookingPrice) {
			setFetchBookingPrice(false);
			onFetchBookingPrice(
				bookingData.id,
				(
					bookingData.status === 'active' ||
						nowSwitch ||
						areDatesEqual(new Date(bookingData?.periodStart), selectedStartDate)
				) ?
					null
				:	selectedStartDate,
				timeNotChanged ? null : selectedEndDate,
				applyingDiscount ?
					Number.isNaN(discount) ?
						0
					:	discount
				: timeNotChanged ? 0
				: null,
				!isUndefined(bookingData.promoCode) ? bookingData.promoCode : null,
				nowSwitch ? true : null,
				timeNotChanged ? false : null
			);
			if (hasChangedItem) {
				setHasChangedItem(false);
			}
		}
	}, [fetchBookingPrice, bookingPriceData]);

	useEffect(() => {
		if (isObject(bookingData) && bookingData !== null && timeNotChanged) {
			onFetchBookingPrice(
				bookingData.id,
				(
					bookingData.status === 'active' ||
						nowSwitch ||
						areDatesEqual(new Date(bookingData?.periodStart), selectedStartDate)
				) ?
					null
				:	selectedStartDate,
				selectedEndDate,
				null,
				!isUndefined(bookingData.promoCode) ? bookingData.promoCode : null,
				nowSwitch ? true : null,
				timeNotChanged ? false : null
			);
		}
	}, [bookingData, onFetchBookingPrice, timeNotChanged]);

	useEffect(() => {
		if (isObject(calculatedPriceData) && !calculatedPriceLoading && !calculatedPriceError) {
			setPrice(calculatedPriceData);
		}
	}, [calculatedPriceLoading]);

	useEffect(() => {
		if (isObject(bookingPriceData) && !bookingPriceLoading && !bookingPriceError) {
			setPrice(bookingPriceData);
		}
	}, [bookingPriceData]);

	useEffect(() => {
		if (isObject(bookingData) && !bookingLoading) {
			let priceDetails;
			if (isObject(bookingData) && isObject(bookingData.priceDetails)) {
				priceDetails = bookingData.priceDetails;
			}
			if (!isUndefined(priceDetails?.discount)) {
				setDiscount(priceDetails?.discount);
				if (priceDetails?.discount > 0) {
					setApplyingDiscount(true);
				}
			}
			setPrice(priceDetails);
		}
	}, [bookingData, bookingLoading, presetData]);

	useEffect(() => {
		if (
			isObject(bookingData) &&
			selectedUser?.id !== bookingData?.userReference?.id &&
			!paymentMethods.find((method) => method.value === bookingData?.billingType)
		) {
			setPaymentMethodAllowed(false);
			setSelectedPaymentMethod('');
		} else {
			setPaymentMethodAllowed(true);
			if (isObject(bookingData)) {
				setSelectedPaymentMethod(bookingData.billingType);
			}
		}
	}, [bookingData, selectedUser, paymentMethods]);

	const paymentMethods2 =
		isOneOffPayment ?
			[
				// Only if not anonymous
				...(selectedUser.email != null ?
					[
						{
							value: 'personal',
							type: 'Direct payment',
							alt: getInitials(selectedUser.name),
							details: selectedUser.name,
							paymentType: fetchItemPoliciesData?.supportedPaymentType,
						},
					]
				:	[]),
				...paymentMethods.filter((el) => el.value !== 'personal'),
			]
		:	paymentMethods;

	const ListItemPaymentMethodContent = ({ el }) => (
		<>
			<ListItemAvatar>
				<Avatar
					src={el.logo}
					variant={el.logo != null ? 'square' : undefined}
					slotProps={{ img: { style: { objectFit: 'contain' } } }}
				>
					{el.alt}
				</Avatar>
			</ListItemAvatar>
			<ListItemText primary={el.type} secondary={el.details} />
			<Radio
				edge='end'
				checked={
					el.value === (isObject(bookingData) ? bookingData?.billingType : selectedPaymentMethod)
				}
				disabled={bookingData != null}
			/>
		</>
	);

	return (
		<div className={classes.usageCard}>
			<Typography className={classes.usageCardHeaders} variant='h5'>
				{t('ui.label.paymentMethod')}
			</Typography>
			{!isObject(selectedItem) ?
				<Notification
					className={classes.usageCardHeaders}
					message={t('views.planboard.addBooking.notification.noItemSelected')}
					startIcon={<FeedbackIcon />}
				/>
			: !selectedStartDate || !selectedEndDate ?
				<Notification
					className={classes.usageCardHeaders}
					message={t('views.planboard.addBooking.notification.noDatesSelected')}
					startIcon={<FeedbackIcon />}
				/>
			: !isObject(selectedUser) ?
				<Notification
					className={classes.usageCardHeaders}
					message={t('views.planboard.addBooking.notification.noUserSelected')}
					startIcon={<FeedbackIcon />}
				/>
			: pmLoading ?
				<List>
					<ListItem className={classes.loadingListItem}>
						<LoadingBar />
					</ListItem>
					<ListItem className={classes.loadingListItem}>
						<LoadingBar />
					</ListItem>
				</List>
			: isEmptyArray(paymentMethods2) || !paymentMethodAllowed ?
				<Notification
					className={classes.usageCardHeaders}
					message={t('views.planboard.addBooking.notification.noPaymentMethodsAvailable')}
					startIcon={<CreditCardIcon />}
					variant='error'
				/>
			:	<List>
					{paymentMethods2.map((el, i, arr) =>
						!isObject(bookingData) ?
							<ListItemButton
								key={i}
								sx={{ pl: 0 }}
								divider={i < arr.length - 1}
								onClick={() => setSelectedPaymentMethod(el.value)}
							>
								<ListItemPaymentMethodContent el={el} />
							</ListItemButton>
						:	<ListItem key={i} sx={{ pl: 0 }} divider={i < arr.length - 1}>
								<ListItemPaymentMethodContent el={el} />
							</ListItem>
					)}
				</List>
			}
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		planboardBooking: state.details.planboardBooking,
		calculatedPrice: state.details.calculatedPrice,
		bookingPrice: state.details.bookingPrice,
		fetchItemPolicies: state.details.fetchItemPolicies,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchCalculatedPrice: (itemId, params) =>
			dispatch(actions.fetchCalculatedPrice(itemId, params)),
		onFetchBookingPrice: (
			bookingId,
			startDate,
			endDate,
			discount,
			promoCode,
			isStartNow,
			isExtending
		) =>
			dispatch(
				actions.fetchBookingPrice(
					bookingId,
					startDate,
					endDate,
					discount,
					promoCode,
					isStartNow,
					isExtending
				)
			),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(UsageCard);
